import { Utils } from "@comact/crc";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { WritableDraft } from "immer";
import _ from "lodash";
import { convertNodesFromServer, IBasicNode, IBasicNodes, IDetailedNodes, INode, INodeServer } from "./model";

declare global {
    interface IStoreState {
        detailedNodes: IDetailedNodes;
        basicNodes: IBasicNodes;
        currentNodeId: string;
        currentMillNodeId: string;
        currentLocationNodeId: string;
        currentOrganizationNodeId: string;
        aliases: Record<string, string> | null;
    }
}

/**
 * Verifies if two nodes are equals. Nodes are equals if the modification date, the codec node state and the node health are equals.
 * @param a the first node
 * @param b the second node
 * @returns true if these two nodes are equals, false otherwise.
 */
const isEqual = (a: INode, b: INode): boolean => (
    a.modificationDate == b.modificationDate && a.nodeState == b.nodeState && a.desiredMetadata?.lastUpdated == b.desiredMetadata?.lastUpdated
);

const isEqualPartial = (a: IBasicNode, b: IBasicNode): boolean => a.modificationDate == b.modificationDate;

const getNodesRecursively = (state: WritableDraft<IDetailedNodes | IBasicNodes>, from: string): string[] => {
    const childrenNodeIds = _.filter(state, (n) => n.parentId == from).map((n) => n.id);

    if (childrenNodeIds.length == 0) return [from];

    let result: string[] = [from];
    childrenNodeIds.forEach((id) => {
        result = _.concat(result, getNodesRecursively(state, id));
    });

    return result;
};

const useStatsNodes = "nodes";

const detailedNodes = createSlice({
    name: "detailedNodes",
    initialState: null as IDetailedNodes,
    reducers: {
        set: (state, { payload }: PayloadAction<INodeServer[]>) => (
            Utils.slices.set({ state, nextState: convertNodesFromServer(payload), isEqual, useStats: useStatsNodes })
        ),
        patch: (state, { payload }: PayloadAction<INodeServer[]>) => (
            Utils.slices.patch({ state, nextState: convertNodesFromServer(payload), isEqual, useStats: useStatsNodes })
        ),
        deleteRecursive: (state, { payload }: PayloadAction<string>) => ( // The payload is the id of the node on which to start the recursive delete
            Utils.slices.delete({ state, keys: getNodesRecursively(state, payload), useStats: useStatsNodes })
        ),
    },
});

const basicNodes = createSlice({
    name: "basicNodes",
    initialState: null as IBasicNodes,
    reducers: {
        setPartial: (state, { payload }: PayloadAction<IBasicNodes>) => (
            Utils.slices.set({ state, nextState: payload, isEqual: isEqualPartial, useStats: useStatsNodes })
        ),
        patchPartial: (state, { payload }: PayloadAction<IBasicNodes>) => (
            Utils.slices.patch({ state, nextState: payload, isEqual: isEqualPartial, useStats: useStatsNodes })
        ),
        deleteRecursivePartial: (state, { payload }: PayloadAction<string>) => ( // The payload is the id of the node on which to start the recursive delete
            Utils.slices.delete({ state, keys: getNodesRecursively(state, payload), useStats: useStatsNodes })
        ),
    },
});

const currentNodeId = createSlice({
    name: "currentNodeId",
    initialState: null as string,
    reducers: {
        setCurrentNodeId: (_state, { payload }: PayloadAction<string>) => payload,
    },
});

const currentMillNodeId = createSlice({
    name: "currentMillNodeId",
    initialState: null as string,
    reducers: {
        setCurrentMillNodeId: (state, { payload }: PayloadAction<string>) => {
            if (state == payload) {
                console.error("PageNode should not change if same...", state, "vs", payload);
            }
            return payload;
        },
    },
});

const currentLocationNodeId = createSlice({
    name: "currentLocationNodeId",
    initialState: null as string,
    reducers: {
        setCurrentLocationNodeId: (state, { payload }: PayloadAction<string>) => {
            if (state == payload) {
                console.error("PageNode should not change if same...", state, "vs", payload);
            }
            return payload;
        },
    },
});

const currentOrganizationNodeId = createSlice({
    name: "currentOrganizationNodeId",
    initialState: null as string,
    reducers: {
        setCurrentOrganizationNodeId: (state, { payload }: PayloadAction<string>) => {
            if (state == payload) {
                console.error("PageNode should not change if same...", state, "vs", payload);
            }
            return payload;
        },
    },
});

const aliases = createSlice({
    name: "aliases",
    initialState: null as Record<string, string> | null,
    reducers: {
        setAliases: (_state, { payload }: PayloadAction<Record<string, string> | null>) => payload,
        setAlias: (state, { payload }: PayloadAction<{ nodeId: string; alias: string | null; }>) => {
            const nextState: Record<string, string> = state ?? {};
            if (payload.alias) nextState[payload.nodeId] = payload.alias;
            else delete nextState[payload.nodeId];
            return nextState;
        },
    },
});

export const actions = {
    ...detailedNodes.actions,
    ...currentNodeId.actions,
    ...currentMillNodeId.actions,
    ...currentLocationNodeId.actions,
    ...currentOrganizationNodeId.actions,
    ...basicNodes.actions,
    ...aliases.actions,
};

export default {
    [detailedNodes.name]: detailedNodes,
    [currentNodeId.name]: currentNodeId,
    [currentMillNodeId.name]: currentMillNodeId,
    [currentLocationNodeId.name]: currentLocationNodeId,
    [currentOrganizationNodeId.name]: currentOrganizationNodeId,
    [basicNodes.name]: basicNodes,
    [aliases.name]: aliases,
};
